import { useEffect } from "react";
import Box from "@mui/material/Box";
import { isLikelyImageUrl } from "../../utils/string";

/**
 * Pista de slides reutilizable para carousels con flechas.
 * Monta la slide activa y sus vecinas (anterior/siguiente) desplazadas con
 * translateX, de modo que al cambiar `index` se vea el efecto de arrastre.
 * Precarga todas las imágenes de `urls` para que el cambio sea fluido.
 *
 * Props:
 * - urls: lista de urls (una por slide)
 * - index: slide activa
 * - renderSlide(url, slideIndex, isActive): contenido de cada slide
 * - sizer: nodo opcional en flujo que define el tamaño del contenedor
 *   (si se omite, el contenedor debe tener tamaño por `sx`)
 */
const SlideTrack = ({
  urls = [],
  index = 0,
  renderSlide,
  sizer = null,
  sx,
}) => {
  const urlsKey = urls.join("\n");

  useEffect(() => {
    const loaders = urlsKey
      .split("\n")
      .filter((url) => typeof url === "string" && isLikelyImageUrl(url))
      .map((url) => {
        const loader = new window.Image();
        loader.src = url;
        return loader;
      });
    return () => {
      loaders.forEach((loader) => {
        loader.onload = null;
      });
    };
  }, [urlsKey]);

  return (
    <Box sx={{ position: "relative", overflow: "hidden", ...sx }}>
      {sizer}
      {urls.map((url, slideIndex) => {
        const offset = slideIndex - index;
        if (Math.abs(offset) > 1) return null;
        const isActive = offset === 0;
        return (
          <Box
            key={`${url}-${slideIndex}`}
            aria-hidden={!isActive}
            sx={{
              position: "absolute",
              inset: 0,
              transform: `translateX(${offset * 100}%)`,
              transition: "transform 0.35s ease",
              willChange: "transform",
            }}
          >
            {renderSlide(url, slideIndex, isActive)}
          </Box>
        );
      })}
    </Box>
  );
};

export default SlideTrack;
